<template>
    <IconField>
        <InputIcon
            v-if="leftIconClass"
            class="tw:text-gray-200 tw:transition-colors"
            :class="[leftIconClass, { 'tw:text-gray-800': isFocused }]"
        />

        <AutoComplete
            v-bind="$attrs"
            ref="autocompleteRef"
            :suggestions="processedSuggestion"
            :model-value="modelValue"
            :placeholder="placeholder"
            :option-label="field"
            :show-clear="clearable"
            :loading="loading"
            :disabled="disabled"
            :complete-on-focus="openOnFocus"
            :auto-option-focus="keepFirst"
            :option-group-label="primevueGroupLabel"
            :option-group-children="primevueGroupChildren"
            :append-to="appendTo"
            fluid
            :input-class="{ 'tw:!rounded-full': rounded }"
            :pt="{
                overlay: {
                    class: 'tw:mt-1 tw:!py-2 tw:!px-3',
                },

                optionGroup: {
                    class: 'tw:!py-2 tw:!px-3 tw:!text-sm tw:!font-bold',
                },

                option: {
                    class: 'tw:!py-2 tw:!px-3 tw:!text-sm',
                },

                emptyMessage: {
                    class: 'tw:!text-sm tw:!p-2',
                },
            }"
            @update:model-value="onUpdateValue"
            @complete="onSearch"
            @focus="onFocus"
            @blur="onBlur"
            @option-select="onItemSelect"
            @keydown="onKeydown"
            @keyup="onKeyup"
        >
            <template v-if="$slots.default" #option="slotProps">
                <slot v-bind="slotProps" />
            </template>

            <template v-if="hasGrouping" #optiongroup="slotProps">
                <div>
                    {{ slotProps.option[primevueGroupLabel] }}
                </div>
            </template>
        </AutoComplete>
    </IconField>
</template>

<script>
/**
 * EAutocomplete
 *
 * Usage:
 *   <e-autocomplete
 *     v-model="name"
 *     :data="filteredOptions"
 *     placeholder="Type to search..."
 *     @typing="getFilteredOptions">
 *   </e-autocomplete>
 *
 *   <e-autocomplete
 *     v-model="selected"
 *     :data="data"
 *     field="name"
 *     :loading="isLoading"
 *     @select="option => selected = option">
 *     <template #empty>No results found</template>
 *   </e-autocomplete>
 *
 *   <e-autocomplete
 *     v-model="query"
 *     :data="suggestions"
 *     keep-first
 *     open-on-focus
 *     clearable>
 *   </e-autocomplete>
 *
 * All Buefy b-autocomplete props, events, and slots are supported.
 */
export default {
    name: 'EAutocomplete',
    inheritAttrs: false,

    props: {
        modelValue: {
            type: [String, Object, Number],
            default: '',
        },
        data: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: '',
        },
        field: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        openOnFocus: {
            type: Boolean,
            default: false,
        },
        keepFirst: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        clearOnSelect: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            default: null,
        },
        groupField: {
            type: String,
            default: null,
        },
        rounded: {
            type: Boolean,
            default: false,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue', 'typing', 'select', 'clear', 'focus', 'blur', 'keydown', 'keyup'],

    data() {
        return {
            isFocused: false,
        };
    },

    computed: {
        appendTo() {
            return this.appendToBody ? 'body' : 'self';
        },

        leftIconClass() {
            if (!this.icon) return null;
            return this.buildIconClass(this.icon);
        },

        hasGrouping() {
            return this.groupField !== null;
        },

        primevueGroupLabel() {
            return this.hasGrouping ? this.groupField : undefined;
        },

        primevueGroupChildren() {
            return this.hasGrouping ? 'items' : undefined;
        },

        processedSuggestion() {
            if (!this.hasGrouping) {
                return this.data;
            }

            const groupedMap = this.data.reduce((acc, item) => {
                const key = item[this.groupField];
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(item);
                return acc;
            }, {});

            return Object.keys(groupedMap).map(groupKey => {
                return {
                    [this.groupField]: groupKey,
                    items: groupedMap[groupKey],
                };
            });
        },
    },

    methods: {
        buildIconClass(iconName) {
            if (!iconName) {
                return '';
            }

            if (iconName.includes(' ')) {
                return iconName;
            }

            return `uil uil-${iconName}`;
        },

        onSearch(event) {
            this.$emit('typing', event.query);
        },

        onKeydown(event) {
            this.$emit('keydown', event);
        },

        onKeyup(event) {
            this.$emit('keyup', event);
        },

        onUpdateValue(value) {
            const wasCleared = value == null || value === '';
            const previousValue = this.modelValue;

            if (wasCleared && previousValue) {
                this.$emit('clear');
            }

            if (typeof value === 'object' && value !== null) {
                const displayValue = this.field ? value[this.field] : value;
                this.$emit('update:modelValue', displayValue);
            } else {
                this.$emit('update:modelValue', value);
            }
        },

        onItemSelect(event) {
            this.$emit('select', event.value);

            if (this.clearOnSelect) {
                this.$nextTick(() => {
                    this.$emit('update:modelValue', '');
                });
            }
        },

        onFocus(event) {
            this.isFocused = true;
            this.$emit('focus', event);
        },

        onBlur(event) {
            this.isFocused = false;
            this.$emit('blur', event);
        },

        showSuggestions() {
            this.$refs.autocompleteRef?.show?.();
        },
    },
};
</script>

<style scoped lang="scss">
@import 'resources/scss/toolbox.scss';
@import 'resources/scss/_variables.scss';

:deep(.p-autocomplete .p-autocomplete-input) {
    --p-inputtext-placeholder-color: var(--p-surface-400);

    background-color: $input-background-color;
    border: $control-border-width solid $input-border-color;
    border-radius: $input-radius;
    color: $input-color;
    box-shadow: $input-shadow;

    min-height: $input-height;
    line-height: $control-line-height;
    font-size: $size-small;
    transition: border-color 0.2s ease;
    width: 100%;

    &:hover {
        border-color: $input-hover-border-color;
    }

    &:focus,
    &:focus-within {
        border-color: $input-focus-border-color;
        box-shadow: $input-focus-box-shadow-size $input-focus-box-shadow-color;
        outline: none;
    }

    &.p-disabled {
        background-color: $input-disabled-background-color;
        border-color: $input-disabled-border-color;
        box-shadow: none;
        color: $input-disabled-color;
        cursor: not-allowed;
    }
}
</style>
